// Central data source for the 7 initiatives (from brochure)
import {
  Brain,
  Users,
  BookOpen,
  TrendingUp,
  Heart,
  Sprout,
  Flag,
} from "lucide-react";

export const INITIATIVES = [
  {
    slug: "mental-health",
    title: "Mental Health at Workplaces",
    short: "Promoting well-being, reducing stress and creating supportive work environments.",
    accent: "#276749",
    icon: Brain,
    image:
      "https://images.unsplash.com/photo-1573497491208-6b1acb260507?auto=format&fit=crop&w=1400&q=80",
    long: [
      "Mental health is the foundation of productive and fulfilling work. UB Maitreya Foundation partners with employers and communities to normalise conversations around mental wellness, provide accessible counselling, and equip managers with the skills to build psychologically safe teams.",
      "Through workshops, one-on-one interventions and awareness drives, we help organisations reduce burnout, absenteeism and stigma — while enabling employees to bring their best selves to work.",
    ],
    highlights: [
      "Workplace wellness workshops",
      "Confidential counselling access",
      "Manager sensitisation training",
      "Stress & burnout screening drives",
    ],
  },
  {
    slug: "emotional-intelligence-teens",
    title: "Enhancing Emotional Intelligence & Confidence of Teens",
    short: "Empowering teens to build confidence, empathy and a positive mindset.",
    accent: "#DD6B20",
    icon: Users,
    image:
      "https://images.unsplash.com/photo-1543269865-cbf427effbad?auto=format&fit=crop&w=1400&q=80",
    long: [
      "Adolescence is when identity, resilience and voice are shaped. Our teen programs blend group coaching, mentorship and lived experience to help young Indians grow into empathetic, confident adults.",
      "We work with schools and community centres to deliver curriculum on self-awareness, communication, decision-making and leadership.",
    ],
    highlights: [
      "Life-skills curriculum for 13–19 year olds",
      "Peer mentorship circles",
      "Confidence & communication labs",
      "Parent-child dialogue sessions",
    ],
  },
  {
    slug: "education",
    title: "Quality & Skillful Education for All",
    short: "Making quality and skill-based education accessible to all.",
    accent: "#2B4C8C",
    icon: BookOpen,
    image:
      "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1400&q=80",
    long: [
      "Education is the surest path out of poverty and into possibility. We fund and run programs that go beyond textbooks — combining foundational literacy, digital literacy and modern vocational skills for learners from every background.",
      "Our approach centres teachers, communities and learners equally.",
    ],
    highlights: [
      "Scholarships for under-served students",
      "Digital literacy labs",
      "Vocational & skilling bootcamps",
      "Teacher training & resources",
    ],
  },
  {
    slug: "entrepreneurs-msme",
    title: "Supporting Entrepreneurs & MSMEs to Grow",
    short: "Encouraging innovation, creating jobs and strengthening the economy.",
    accent: "#805AD5",
    icon: TrendingUp,
    image:
      "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1400&q=80",
    long: [
      "Small businesses are the engine of the Indian economy. We give first-generation entrepreneurs and MSMEs the mentorship, market linkages and finance-readiness they need to move from surviving to scaling.",
      "From village artisans to urban founders — we make growth resources actually reach the last mile.",
    ],
    highlights: [
      "MSME mentorship networks",
      "Access-to-finance readiness programs",
      "Digital marketing & GST clinics",
      "Market linkage & showcasing",
    ],
  },
  {
    slug: "women-empowerment",
    title: "Women's Empowerment",
    short: "Empowering women to achieve financial independence and leadership.",
    accent: "#B83280",
    icon: Heart,
    image:
      "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=1400&q=80",
    long: [
      "When a woman rises, an entire community rises with her. We invest in women's skilling, enterprise development, financial literacy and leadership — with a special focus on rural and semi-urban geographies.",
      "Every programme is co-designed with the women it serves.",
    ],
    highlights: [
      "Self-Help Group (SHG) formation & training",
      "Financial literacy & savings programs",
      "Micro-enterprise incubation",
      "Leadership fellowships",
    ],
  },
  {
    slug: "natural-farming",
    title: "Supporting Natural Farming",
    short: "Helping farmers learn and market natural farming — healthy food, healthy land.",
    accent: "#38A169",
    icon: Sprout,
    image:
      "https://images.unsplash.com/photo-1500595046743-cd271d694d30?auto=format&fit=crop&w=1400&q=80",
    long: [
      "Natural farming heals soil, saves water, keeps our air clean — and puts real, chemical-free food on Indian plates at prices families can actually afford. We train farmers in regenerative practices and help them access markets that reward the extra care.",
      "This is a long-term bet on the health of India's people and its land.",
    ],
    highlights: [
      "Regenerative agriculture training",
      "Seed & input support",
      "Farmer producer organisation building",
      "Direct-to-consumer market linkages",
    ],
  },
  {
    slug: "civic-sense",
    title: "Making Awareness of Civic Senses — Proud Indians",
    short: "Building responsible citizens for a cleaner, safer and better India.",
    accent: "#2C7A7B",
    icon: Flag,
    image:
      "https://images.unsplash.com/photo-1567606404787-9e0208d95c68?auto=format&fit=crop&w=1400&q=80",
    long: [
      "India rises when Indians take pride in the country they share. Through school programmes, community drives and public campaigns, we nurture civic sense — cleanliness, road safety, respect for public spaces and active citizenship.",
      "Small everyday habits, multiplied across a nation, become a movement.",
    ],
    highlights: [
      "School civic-sense curriculum",
      "Community cleanliness drives",
      "Public awareness campaigns",
      "Youth civic leadership programs",
    ],
  },
];

export const findInitiative = (slug) => INITIATIVES.find((i) => i.slug === slug);

export const DIRECTORS = [
  {
    name: "Swati Pandit",
    role: "Founder Director",
    photo:
      "https://customer-assets.emergentagent.com/job_maitreya-foundation/artifacts/h7189hru_image.png",
    bio:
      "Swati Pandit is the founding heart of UB Maitreya Foundation. Her work in community mental health, women's empowerment and grassroots education shapes the foundation's people-first philosophy.",
  },
  {
    name: "Abha Pandit",
    role: "Founder Director",
    photo:
      "https://customer-assets.emergentagent.com/job_maitreya-foundation/artifacts/17l23fkx_image.png",
    bio:
      "Abha Pandit leads programme design and partnerships. She brings a systems lens to the foundation — connecting corporate partners, educators and farmers into a single, coherent mission.",
  },
];

export const FOUNDATION_LOGO =
  "https://customer-assets.emergentagent.com/job_maitreya-foundation/artifacts/kselioii_image.png";

export const CERTIFICATES = {
  reg_80g_pdf:
    "https://customer-assets.emergentagent.com/job_maitreya-foundation/artifacts/ck8al1gp_AAECU1213CF20261_signed%2080%20g.pdf",
  reg_12a_pdf:
    "https://customer-assets.emergentagent.com/job_maitreya-foundation/artifacts/64p5rs9f_AAECU1213CE20251_signed%2012A.pdf",
};

export const EVENTS = [
  {
    slug: "felicitation-archana-mankape",
    title: "Felicitating Mrs Archana Nilkanth & Mrs Mankape",
    subtitle: "Corporators, Chhatrapati Sambhajinagar Municipal Corporation",
    theme: "Women Power in Governance",
    image:
      "https://customer-assets.emergentagent.com/job_maitreya-foundation/artifacts/rj3s3eyn_image.png",
    description:
      "UB Maitreya Foundation honoured Mrs Archana Nilkanth and Mrs Mankape (Prabhag 20) — Corporators of Chhatrapati Sambhajinagar Municipal Corporation — for their dedication, leadership and commitment to building a stronger community.",
  },
  {
    slug: "felicitation-savita-kulkarni",
    title: "Felicitating Mrs Savita Ratnakar Kulkarni",
    subtitle: "Corporator, Prabhag 26, Chhatrapati Sambhajinagar",
    theme: "Women Power in Governance",
    image:
      "https://customer-assets.emergentagent.com/job_maitreya-foundation/artifacts/46jquqxm_image.png",
    description:
      "Mrs Savita Ratnakar Kulkarni was felicitated by Swati Pandit, Founder Director of UB Maitreya Foundation, in recognition of her public service and leadership as Corporator of Prabhag 26.",
  },
  {
    slug: "felicitation-bharti-sonawane",
    title: "Felicitating Mrs Bharti Sonawane",
    subtitle: "Corporator, Chhatrapati Sambhajinagar",
    theme: "Women Power in Governance",
    image:
      "https://customer-assets.emergentagent.com/job_maitreya-foundation/artifacts/bjap03i0_image.png",
    description:
      "A warm meeting between Mrs Bharti Sonawane, Corporator of Chhatrapati Sambhajinagar, and Swati Pandit — celebrating women's leadership at the grassroots of Indian governance.",
  },
];

export const FOUNDATION_INFO = {
  name: "UB Maitreya Foundation",
  tagline: "For Growth. For Harmony. For All.",
  pan: "AAECU1213C",
  cin: "U88900MH2026NPL469696",
  reg80g: "AAECU1213CF2026101",
  reg80g_date: "02-04-2026",
  ay_validity: "AY 2026-27 to AY 2028-29",
  darpan: "MH/2026/1042775",
  bank: "IndusInd Bank",
  account: "201037484555",
  ifsc: "INDB0001937",
  branch: "Kurla",
  phone: "+91 9561885572",
  email: "info@ubmaitreyafoundation.org",
  address:
    "1301 Bldg No 154, CTS No. 356, Mukhtidham CHS, Vikhroli, Mumbai, Maharashtra 400083",
};
